import { useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Card, Col, Descriptions, Popconfirm, Row, Skeleton, Space, Tag, Typography } from 'antd';
import { SoundOutlined, UndoOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { errMsg } from '../../lib/errors';
import { formatWhen } from '../listeners/listenerStats';
import {
  RINGTONE_ACCEPT,
  ROLE_COPY,
  formatBytes,
  ringtoneFileProblem,
  type AdminRingtone,
  type RingtoneRole,
} from './ringtones';

const QUERY_KEY = ['admin', 'ringtones'];

/**
 * Admin → Ringtones. One card per receiver role: what plays now (uploaded file
 * or the app's built-in tone), a preview, Upload and Reset to built-in.
 * Phones pick up a change the next time the app starts.
 */
export function RingtonesPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => (await api.get<AdminRingtone[]>('/admin/ringtones')).data,
  });
  const byRole = (role: RingtoneRole) => data?.find((r) => r.role === role);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space direction="vertical" size={0}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Ringtones
        </Typography.Title>
        <Typography.Text type="secondary">
          Upload a different incoming-call tone for listeners and for normal users. Phones download it the next time
          the app starts; until then, and whenever no file is uploaded, the app plays its built-in tone.
        </Typography.Text>
      </Space>

      {isError ? <Alert type="error" showIcon message={errMsg(error, 'Could not load the ringtones')} /> : null}

      <Row gutter={[16, 16]}>
        {(['listener', 'user'] as const).map((role) => (
          <Col xs={24} xl={12} key={role}>
            {isLoading ? <Card><Skeleton active /></Card> : <RingtoneCard role={role} current={byRole(role)} />}
          </Col>
        ))}
      </Row>

      <Alert
        type="info"
        showIcon
        message="Tips for a good ringtone"
        description="MP3, WAV, OGG or M4A, up to 1 MB. A short tune of 3–10 seconds that loops cleanly works best — the phone repeats it until the call is answered. Only upload sounds you have the right to use."
      />
    </Space>
  );
}

function RingtoneCard({ role, current }: { role: RingtoneRole; current: AdminRingtone | undefined }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const copy = ROLE_COPY[role];
  const custom = current?.custom === true;

  const upload = useMutation({
    mutationFn: async (file: File) =>
      (
        await api.put<AdminRingtone>(`/admin/ringtones/${role}`, file, {
          headers: {
            'content-type': file.type || 'application/octet-stream',
            'x-file-name': encodeURIComponent(file.name),
          },
          timeout: 60_000,
        })
      ).data,
    onSuccess: (r) => {
      message.success(`${copy.title} updated to “${r.fileName}”. Phones switch on their next app start.`);
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e) => message.error(errMsg(e, 'Could not upload the ringtone')),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.delete<AdminRingtone>(`/admin/ringtones/${role}`)).data,
    onSuccess: () => {
      message.success(`${copy.title} is back to the built-in tone.`);
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e) => message.error(errMsg(e, 'Could not reset the ringtone')),
  });

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // picking the same file again must fire onChange again
    if (!file) return;
    const problem = ringtoneFileProblem(file);
    if (problem) {
      message.error(problem);
      return;
    }
    upload.mutate(file);
  };

  return (
    <Card
      title={
        <Space>
          <SoundOutlined />
          {copy.title}
          {custom ? <Tag color="magenta">Custom</Tag> : <Tag>Built-in</Tag>}
        </Space>
      }
    >
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        <Typography.Text type="secondary">{copy.who}</Typography.Text>

        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Playing now">{custom ? current?.fileName : copy.builtIn}</Descriptions.Item>
          {custom ? (
            <>
              <Descriptions.Item label="Size">{formatBytes(current?.sizeBytes)}</Descriptions.Item>
              <Descriptions.Item label="Uploaded">{formatWhen(current?.updatedAt)}</Descriptions.Item>
            </>
          ) : null}
        </Descriptions>

        {custom && current?.version ? (
          <CustomPreview role={role} version={current.version} />
        ) : (
          <audio controls preload="none" src={copy.builtInFile} style={{ width: '100%' }} aria-label={`${copy.title} preview`} />
        )}

        <Can permission="ringtones.edit">
          <Space wrap>
            <input
              ref={input}
              type="file"
              accept={RINGTONE_ACCEPT}
              onChange={onPick}
              style={{ display: 'none' }}
              aria-label={`Choose ${copy.title.toLowerCase()} file`}
            />
            <Button type="primary" icon={<UploadOutlined />} loading={upload.isPending} onClick={() => input.current?.click()}>
              {custom ? 'Replace ringtone' : 'Upload ringtone'}
            </Button>
            {custom ? (
              <Popconfirm
                title="Go back to the built-in tone?"
                description="Phones switch back on their next app start."
                okText="Reset"
                onConfirm={() => reset.mutate()}
              >
                <Button icon={<UndoOutlined />} loading={reset.isPending}>
                  Reset to built-in
                </Button>
              </Popconfirm>
            ) : null}
          </Space>
        </Can>
      </Space>
    </Card>
  );
}

/** The uploaded file is behind admin auth, so fetch it and play a blob URL. */
function CustomPreview({ role, version }: { role: RingtoneRole; version: string }) {
  const { data: blob, isError } = useQuery({
    queryKey: ['admin', 'ringtones', role, 'file', version],
    queryFn: async () => (await api.get<Blob>(`/admin/ringtones/${role}/file`, { responseType: 'blob' })).data,
    staleTime: Infinity,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) return undefined;
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  if (isError) return <Alert type="warning" showIcon message="Could not load the preview." />;
  if (!url) return <Skeleton.Input active block />;
  return <audio controls src={url} style={{ width: '100%' }} aria-label={`${ROLE_COPY[role].title} preview`} />;
}
